/** Where a label may start a word: a space, `&`, a hyphen or a slash. */
const WORD_BREAK = /[\s&/-]+/

/**
 * How well a row answers a search, lower is better, or `undefined` when it does
 * not match. The label and every keyword are compared the same way; keywords
 * are searched but never shown.
 *
 * - 0: the label or a keyword starts with the query
 * - 1: a word of the label starts with the query
 * - 2: the label or a keyword contains the query
 */
function rankOf(option: { label: string; keywords?: string[] }, query: string): number | undefined {
  const label = option.label.toLowerCase()
  const texts = [label, ...(option.keywords ?? []).map((keyword) => keyword.toLowerCase())]
  if (texts.some((text) => text.startsWith(query))) return 0
  if (label.split(WORD_BREAK).some((word) => word.startsWith(query))) return 1
  if (texts.some((text) => text.includes(query))) return 2
  return undefined
}

/**
 * The options a search leaves, best match first. An empty query keeps every
 * option as given. Within one rank the given order is kept, so a tree stays a
 * tree when it is searched.
 */
export function rankOptions<T extends { label: string; keywords?: string[] }>(
  options: T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase()
  if (needle === '') return options
  const ranked = options.flatMap((option) => {
    const rank = rankOf(option, needle)
    return rank === undefined ? [] : [{ option, rank }]
  })
  // `Array.prototype.sort` is stable, so equal ranks keep their given order.
  return ranked.sort((a, b) => a.rank - b.rank).map(({ option }) => option)
}

/**
 * The next row a keyboard can reach from `active` (`-1` for none) in
 * `direction`, skipping disabled rows and wrapping at both ends. `-1` when no
 * row can be reached.
 */
export function nextEnabledIndex<T extends { disabled?: boolean }>(
  options: T[],
  active: number,
  direction: 1 | -1,
): number {
  const count = options.length
  // From "none", down starts at the first row and up at the last one.
  let index = active < 0 ? (direction === 1 ? -1 : count) : active
  for (let step = 0; step < count; step++) {
    index = (index + direction + count) % count
    if (!options[index]?.disabled) return index
  }
  return -1
}
